import React, { useEffect, useState, useCallback } from 'react';
import {
    Card, Tabs, Table, Tag, Button, Space, Statistic, Row, Col, Segmented, Modal, Form, Input,
    InputNumber, Switch, Popconfirm, Empty, message
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, UnorderedListOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { dentalService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import InvoicesTab from '../../components/Dental/InvoicesTab';
import { formatMoney, installmentStatus, translate } from '../../utils/dental';

// The dental clinic's own page: who owes what and when, and the price list.
const Dental = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isDoctor = user?.role === 'doctor';

    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [view, setView] = useState('all');
    const [procedures, setProcedures] = useState([]);
    const [loading, setLoading] = useState(false);
    // The price-list line being written: null, {} for a new one, or the line.
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const tr = (key, fallback) => translate(t, key, fallback);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [installments, list] = await Promise.all([
                dentalService.getInstallments(),
                dentalService.getProcedures()
            ]);
            setRows(installments.data || []);
            setSummary(installments.summary || {});
            setProcedures(list.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load();
    }, [load]);

    const openPatient = (row) => {
        if (row.patient?._id) navigate(`/${user.role}/patient/${row.patient._id}?tab=dental`);
    };

    const soon = dayjs().add(7, 'day').endOf('day');
    const shown = rows.filter((row) => {
        if (view === 'overdue') return row.status === 'overdue';
        if (view === 'week') return row.status !== 'overdue' && dayjs(row.dueDate).isBefore(soon);
        return true;
    });

    const openProcedure = (procedure) => {
        form.resetFields();
        form.setFieldsValue(procedure?._id ? procedure : { isActive: true });
        setEditing(procedure || {});
    };

    const saveProcedure = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (error) {
            return;
        }
        setSaving(true);
        try {
            if (editing._id) await dentalService.updateProcedure(editing._id, values);
            else await dentalService.createProcedure(values);
            message.success(t('success'));
            setEditing(null);
            load();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSaving(false);
        }
    };

    const removeProcedure = async (id) => {
        try {
            await dentalService.deleteProcedure(id);
            message.success(t('success'));
            load();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const installmentsTab = (
        <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic title={tr('dentalOwed', 'Still to come in')} value={formatMoney(summary.total)} valueStyle={{ fontSize: 20 }} />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title={installmentStatus.label(t, 'overdue')}
                            value={formatMoney(summary.overdueTotal)}
                            valueStyle={{ fontSize: 20, color: summary.overdueCount > 0 ? '#cf1322' : undefined }}
                            suffix={summary.overdueCount > 0 ? `(${summary.overdueCount})` : undefined}
                        />
                    </Card>
                </Col>
            </Row>

            <Segmented
                style={{ marginBottom: 12 }}
                value={view}
                onChange={setView}
                options={[
                    { value: 'all', label: t('all') },
                    { value: 'overdue', label: installmentStatus.label(t, 'overdue') },
                    { value: 'week', label: tr('dentalDueThisWeek', 'Due within 7 days') }
                ]}
            />

            <Table
                rowKey={(row) => `${row.planId}-${row._id || row.dueDate}`}
                loading={loading}
                dataSource={shown}
                scroll={{ x: 760 }}
                pagination={{ pageSize: 20 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={tr('dentalNoInstallments', 'No instalments owed')} /> }}
                onRow={(row) => ({ onClick: () => openPatient(row), style: { cursor: row.patient ? 'pointer' : 'default' } })}
                columns={[
                    {
                        title: tr('dueDate', 'Due date'),
                        dataIndex: 'dueDate',
                        width: 130,
                        render: (value) => dayjs(value).format('YYYY-MM-DD')
                    },
                    {
                        title: t('patients'),
                        render: (_, row) => (
                            <Space direction="vertical" size={0}>
                                <span>{row.patient?.name || '-'}</span>
                                {row.patient?.phoneNumber && <small style={{ color: '#888' }}>{row.patient.phoneNumber}</small>}
                            </Space>
                        )
                    },
                    { title: tr('dentalPlan', 'Treatment plan'), dataIndex: 'planTitle' },
                    { title: tr('amount', 'Amount'), dataIndex: 'amount', align: 'end', render: (value) => formatMoney(value) },
                    { title: tr('remaining', 'Remaining'), dataIndex: 'remaining', align: 'end', render: (value) => <strong>{formatMoney(value)}</strong> },
                    {
                        title: t('status'),
                        dataIndex: 'status',
                        width: 120,
                        render: (status) => <Tag color={installmentStatus.color(status)}>{installmentStatus.label(t, status)}</Tag>
                    }
                ]}
            />
        </>
    );

    const priceListTab = (
        <>
            {isDoctor && (
                <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 12 }} onClick={() => openProcedure(null)}>
                    {tr('dentalAddProcedure', 'Add procedure')}
                </Button>
            )}
            <Table
                rowKey="_id"
                loading={loading}
                dataSource={procedures}
                scroll={{ x: 520 }}
                pagination={{ pageSize: 20 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={tr('dentalNoProcedures', 'The price list is empty')} /> }}
                columns={[
                    { title: tr('dentalProcedureName', 'Procedure'), dataIndex: 'name' },
                    { title: tr('price', 'Price'), dataIndex: 'price', align: 'end', width: 180, render: (value) => formatMoney(value) },
                    {
                        title: t('status'),
                        dataIndex: 'isActive',
                        width: 120,
                        render: (active) => (active ? <Tag color="green">{t('active')}</Tag> : <Tag>{t('inactive')}</Tag>)
                    },
                    ...(isDoctor ? [{
                        width: 110,
                        render: (_, procedure) => (
                            <Space>
                                <Button icon={<EditOutlined />} onClick={() => openProcedure(procedure)} />
                                <Popconfirm title={t('confirmDelete')} onConfirm={() => removeProcedure(procedure._id)}>
                                    <Button danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </Space>
                        )
                    }] : [])
                ]}
            />
        </>
    );

    return (
        <div className="page-container">
            <h1 style={{ marginTop: 0 }}>{tr('dentalClinic', 'Dental clinic')}</h1>
            <Card>
                <Tabs
                    items={[
                        {
                            key: 'installments',
                            label: <span><DollarOutlined /> {tr('installments', 'Instalments')}{rows.length > 0 ? ` (${rows.length})` : ''}</span>,
                            children: installmentsTab
                        },
                        {
                            key: 'invoices',
                            label: <span><FileTextOutlined /> {tr('invoices', 'Invoices')}</span>,
                            children: <InvoicesTab />
                        },
                        {
                            key: 'prices',
                            label: <span><UnorderedListOutlined /> {tr('dentalPriceList', 'Price list')}</span>,
                            children: priceListTab
                        }
                    ]}
                />
            </Card>

            <Modal
                title={editing?._id ? t('edit') : tr('dentalAddProcedure', 'Add procedure')}
                open={Boolean(editing)}
                onCancel={() => setEditing(null)}
                onOk={saveProcedure}
                okText={t('save')}
                confirmLoading={saving}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label={tr('dentalProcedureName', 'Procedure')} rules={[{ required: true, message: t('required') }]}>
                        <Input placeholder={tr('dentalProcedureExample', 'e.g. Root canal, Crown, Scaling')} />
                    </Form.Item>
                    <Form.Item name="price" label={tr('price', 'Price')}>
                        <InputNumber
                            min={0}
                            step={5000}
                            style={{ width: '100%' }}
                            formatter={(value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value.replace(/,/g, '')}
                            addonAfter="IQD"
                        />
                    </Form.Item>
                    <Form.Item name="isActive" label={t('active')} valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Dental;
